import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '../components/StateViews'
import { api } from '../lib/api'
import { money, number, relativeDate } from '../lib/format'
import { useApi } from '../lib/useApi'

type WaitlistPayload = {
  currency: string
  rows: {
    variantId: string
    productId: string
    productTitle: string | null
    variantTitle: string | null
    imageUrl: string | null
    waitingSubscribers: number
    demandValueMinor: number
    inStock: boolean
  }[]
  hasMore: boolean
  nextOffset: number | null
}

/** Waitlist explorer (SW-603). */
export function Waitlist() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<'waiting' | 'recent' | 'value'>('waiting')
  const { data, error, loading, reload } = useApi<WaitlistPayload>(
    `/waitlist?sort=${sort}&limit=50${search ? `&search=${encodeURIComponent(search)}` : ''}`,
  )

  if (loading && !data) return <LoadingState label="Loading waitlists" />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data) return null

  return (
    <s-stack direction="block" gap="large">
      <s-section>
        <s-stack direction="inline" gap="base" alignItems="end">
          <s-search-field
            label="Search products"
            value={search}
            placeholder="Product title or SKU"
            onInput={(event: Event) => setSearch((event.target as HTMLInputElement).value)}
          />
          <s-select
            label="Sort"
            value={sort}
            onChange={(event: Event) =>
              setSort((event.target as HTMLSelectElement).value as typeof sort)
            }
          >
            <s-option value="waiting">Most waiting</s-option>
            <s-option value="value">Highest demand value</s-option>
            <s-option value="recent">Most recent signup</s-option>
          </s-select>
          <s-button onClick={() => api.download('/export/waitlist.csv', 'stockwait-waitlist.csv')}>
            Export CSV
          </s-button>
        </s-stack>
      </s-section>

      <s-section>
        {data.rows.length === 0 ? (
          <EmptyState heading="No waitlists match">
            Nothing here yet. Waitlists appear as shoppers hit sold-out products with the button
            live.
          </EmptyState>
        ) : (
          <s-table>
            <s-table-header-row>
              <s-table-header>Product</s-table-header>
              <s-table-header>Waiting</s-table-header>
              <s-table-header>Demand</s-table-header>
              <s-table-header>Stock</s-table-header>
              <s-table-header />
            </s-table-header-row>
            <s-table-body>
              {data.rows.map((row) => (
                <s-table-row key={row.variantId}>
                  <s-table-cell>
                    <s-stack direction="inline" gap="small-300" alignItems="center">
                      {row.imageUrl ? <s-thumbnail src={row.imageUrl} alt="" size="small" /> : null}
                      <s-stack direction="block">
                        <s-text type="strong">{row.productTitle ?? 'Product'}</s-text>
                        {row.variantTitle && row.variantTitle !== 'Default Title' ? (
                          <s-text color="subdued">{row.variantTitle}</s-text>
                        ) : null}
                      </s-stack>
                    </s-stack>
                  </s-table-cell>
                  <s-table-cell>{number(row.waitingSubscribers)}</s-table-cell>
                  <s-table-cell>{money(row.demandValueMinor, data.currency)}</s-table-cell>
                  <s-table-cell>
                    <s-badge tone={row.inStock ? 'success' : 'neutral'}>
                      {row.inStock ? 'In stock' : 'Sold out'}
                    </s-badge>
                  </s-table-cell>
                  <s-table-cell>
                    <s-button
                      variant="tertiary"
                      onClick={() => navigate(`/waitlist/${row.variantId}`)}
                    >
                      View
                    </s-button>
                  </s-table-cell>
                </s-table-row>
              ))}
            </s-table-body>
          </s-table>
        )}
      </s-section>
      <ShopperLookup />
    </s-stack>
  )
}

type VariantPayload = {
  variant: {
    product_title: string | null
    variant_title: string | null
    available_qty: number
  } | null
  subscribers: {
    id: string
    email: string | null
    status: string
    created_at: number
    notified_at: number | null
    consent_source: string
    consent_at: number
  }[]
  recentDecisions: {
    id: string
    detectedAt: number
    availableQty: number
    subscriberCount: number
    dispatchedAt: number | null
    source: string
    decision: { reason: string; shouldAlert: boolean } | null
  }[]
}

/** Variant drill-down, including the decision trail that answers the top support question. */
export function VariantDetail() {
  const { variantId } = useParams()
  const navigate = useNavigate()
  const { data, error, loading, reload } = useApi<VariantPayload>(`/waitlist/${variantId}`)
  const [sending, setSending] = useState(false)

  if (loading && !data) return <LoadingState />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data) return null

  async function sendNow() {
    setSending(true)
    try {
      const result = await api.post<{ sent: boolean; message: string }>(
        `/waitlist/${variantId}/send`,
      )
      shopify.toast.show(result.message, { isError: !result.sent })
      reload()
    } catch {
      shopify.toast.show('Could not send', { isError: true })
    } finally {
      setSending(false)
    }
  }

  return (
    <s-stack direction="block" gap="large">
      <s-section>
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-stack direction="block" gap="small-500">
            <s-heading>{data.variant?.product_title ?? 'Variant'}</s-heading>
            <s-text color="subdued">
              {data.variant?.variant_title} · {number(data.subscribers.length)} on the list
            </s-text>
          </s-stack>
          <s-stack direction="inline" gap="small-300">
            <s-button variant="tertiary" onClick={() => navigate('/waitlist')}>
              Back
            </s-button>
            {/* Gated by the same cooldown as an automatic send, so it cannot double-send. */}
            <s-button variant="primary" disabled={sending} onClick={sendNow}>
              {sending ? 'Sending…' : 'Send alert now'}
            </s-button>
          </s-stack>
        </s-stack>
      </s-section>

      {/*
        The decision trail, surfaced to the merchant rather than kept for support. "Why didn't
        my customers get an email?" is the most common question in this category, and this
        answers it without a ticket.
      */}
      <s-section heading="Recent restock decisions">
        {data.recentDecisions.length === 0 ? (
          <s-paragraph color="subdued">
            No inventory changes recorded for this variant yet.
          </s-paragraph>
        ) : (
          <s-table>
            <s-table-header-row>
              <s-table-header>When</s-table-header>
              <s-table-header>Quantity seen</s-table-header>
              <s-table-header>Outcome</s-table-header>
              <s-table-header>Alerts</s-table-header>
              <s-table-header>Source</s-table-header>
            </s-table-header-row>
            <s-table-body>
              {data.recentDecisions.map((decision) => (
                <s-table-row key={decision.id}>
                  <s-table-cell>{relativeDate(decision.detectedAt)}</s-table-cell>
                  <s-table-cell>{decision.availableQty}</s-table-cell>
                  <s-table-cell>
                    <s-badge tone={decision.decision?.shouldAlert ? 'success' : 'neutral'}>
                      {explainReason(decision.decision?.reason ?? 'unknown')}
                    </s-badge>
                  </s-table-cell>
                  <s-table-cell>{decision.subscriberCount}</s-table-cell>
                  <s-table-cell>{decision.source}</s-table-cell>
                </s-table-row>
              ))}
            </s-table-body>
          </s-table>
        )}
      </s-section>

      <s-section heading="Shoppers waiting">
        <s-table>
          <s-table-header-row>
            <s-table-header>Email</s-table-header>
            <s-table-header>Joined</s-table-header>
            <s-table-header>Status</s-table-header>
            <s-table-header>Notified</s-table-header>
            <s-table-header />
          </s-table-header-row>
          <s-table-body>
            {data.subscribers.map((subscriber) => (
              <s-table-row key={subscriber.id}>
                <s-table-cell>{subscriber.email ?? '(redacted)'}</s-table-cell>
                <s-table-cell>{relativeDate(subscriber.created_at)}</s-table-cell>
                <s-table-cell>
                  <s-badge tone={subscriber.status === 'active' ? 'info' : 'neutral'}>
                    {subscriber.status}
                  </s-badge>
                </s-table-cell>
                <s-table-cell>
                  {subscriber.notified_at ? relativeDate(subscriber.notified_at) : '-'}
                </s-table-cell>
                <s-table-cell>
                  {/*
                    Shoppers do ask the merchant to take them off a list rather than using the
                    link in the email, by reply, by phone, in a DM. Without this the merchant
                    has to open a support ticket with us to action a request they were given
                    directly, which is a bad answer to give someone withdrawing consent.
                  */}
                  {subscriber.status === 'active' ? (
                    <s-button
                      variant="tertiary"
                      tone="critical"
                      onClick={async () => {
                        await api.post(`/subscribers/${subscriber.id}/remove`)
                        shopify.toast.show('Removed from the waitlist')
                        reload()
                      }}
                    >
                      Remove
                    </s-button>
                  ) : null}
                </s-table-cell>
              </s-table-row>
            ))}
          </s-table-body>
        </s-table>
      </s-section>
    </s-stack>
  )
}

/** Decision reasons in merchant language. `below_threshold` means nothing to a shop owner. */
function explainReason(reason: string): string {
  const map: Record<string, string> = {
    restocked: 'Alerts sent',
    policy_opened: 'Alerts sent (backorder opened)',
    manual_override: 'Alerts sent (manual)',
    no_subscribers: 'Nobody waiting',
    not_available: 'Still sold out',
    no_transition: 'Was already in stock',
    below_threshold: 'Too few units restocked',
    within_cooldown: 'Alerted recently',
    product_excluded: 'Product excluded',
    product_inactive: 'Product not active',
    daily_cap_reached: 'Daily cap reached',
  }
  return map[reason] ?? reason
}

type LookupResult = {
  subscriptions: {
    id: string
    variantId: string
    status: string
    createdAt: number
    notifiedAt: number | null
    consent: { source: string; at: number; copyVersion: string | null; ip: string | null }
    deliveries: { id: string; channel: string; status: string; sent_at: number | null }[]
  }[]
}

/**
 * Find one shopper across every waitlist they are on (SW-7).
 *
 * The support question this answers is "someone emailed me asking why they never got their
 * alert"; which needs the consent record and the delivery attempts together, for one person,
 * across products. The merchant had no way to answer it and no way to reach the endpoint that
 * could; asking us meant asking someone with no visibility into their shop either.
 *
 * Searching writes a `data_access_log` entry server-side, hashed and never storing the address,
 * because a targeted lookup of one named person is exactly the access a subject-access request
 * asks about later.
 */
export function ShopperLookup() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState<string | null>(null)
  const { data, error, loading } = useApi<LookupResult>(
    submitted === null ? null : `/subscribers/lookup?email=${encodeURIComponent(submitted)}`,
  )

  return (
    <s-section heading="Find a shopper">
      <s-stack direction="block" gap="base">
        <s-paragraph color="subdued">
          Look someone up by the address they signed up with, to see what they are waiting for and
          whether their alerts were delivered.
        </s-paragraph>

        <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="end">
          <s-email-field
            label="Email address"
            value={email}
            placeholder="shopper@example.com"
            onChange={(event: Event) => setEmail((event.target as HTMLInputElement).value)}
          />
          <s-button
            disabled={email.trim() === '' || loading}
            onClick={() => setSubmitted(email.trim())}
          >
            {loading ? 'Searching…' : 'Search'}
          </s-button>
        </s-grid>

        {error ? <ErrorState error={error} /> : null}

        {data && submitted !== null ? (
          data.subscriptions.length === 0 ? (
            <s-paragraph color="subdued">
              Nobody with that address is on a waitlist for this shop.
            </s-paragraph>
          ) : (
            <s-table>
              <s-table-header-row>
                <s-table-header>Status</s-table-header>
                <s-table-header>Joined</s-table-header>
                <s-table-header>Consent</s-table-header>
                <s-table-header>Alerts</s-table-header>
              </s-table-header-row>
              <s-table-body>
                {data.subscriptions.map((sub) => (
                  <s-table-row key={sub.id}>
                    <s-table-cell>
                      <s-badge tone={sub.status === 'active' ? 'info' : 'neutral'}>
                        {sub.status}
                      </s-badge>
                    </s-table-cell>
                    <s-table-cell>{relativeDate(sub.createdAt)}</s-table-cell>
                    <s-table-cell>
                      {/* The answer to "did they actually opt in?", which is the whole
                          reason the consent columns are kept. */}
                      {sub.consent.source} · {relativeDate(sub.consent.at)}
                    </s-table-cell>
                    <s-table-cell>
                      {sub.deliveries.length === 0
                        ? '-'
                        : sub.deliveries
                            .map((delivery) => `${delivery.channel}: ${delivery.status}`)
                            .join(', ')}
                    </s-table-cell>
                  </s-table-row>
                ))}
              </s-table-body>
            </s-table>
          )
        ) : null}
      </s-stack>
    </s-section>
  )
}
